import React from 'react';
import { Box, Typography, Avatar, useTheme, useMediaQuery } from '@mui/material';
import { colors } from '../../theme/themeConfig';

interface PageHeaderProps {
  username?: string; // Make username optional
  photoUrl?: string;
  greeting?: string;
  title?: string;
  emoji?: string;
  balance?: number;
  subtitle?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  username,
  photoUrl,
  greeting = 'Привет',
  title,
  emoji = '👋',
  balance,
  subtitle,
}) => {
  const theme = useTheme();
  // Using a single breakpoint at 375px as per user preference
  const isMobile = useMediaQuery('(max-width:375px)');

  return (
    <Box
      sx={{
        position: 'relative',
        marginBottom: isMobile ? '16px' : '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch', // Override parent's alignItems
        alignSelf: 'stretch',  // Take full width regardless of parent's alignment
        width: '100%',         // Ensure full width
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {title ? (
            <>
              <Typography
                variant="h1"
                sx={{
                  fontSize: '22px',
                  fontWeight: 500,
                  // marginBottom: '1px', // No margin bottom
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: isMobile ? '4px' : 0,
                  color: colors.text.primary,
                }}
              >
                {title}
              </Typography>
              
              {/* Subtitle directly under title in the same column */}
              {subtitle && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: isMobile ? '14px' : '16px',
                    color: colors.text.secondary,
                    // marginTop: '-1px', // Negative margin to pull it up
                    paddingLeft: isMobile ? '4px' : 0,
                    lineHeight: 1.1, // Even tighter line height
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </>
          ) : (
            <Typography
              variant="h1"
              sx={{
                fontSize: '22px',
                fontWeight: 500,
                marginBottom: isMobile ? '8px' : '12px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: isMobile ? '4px' : 0,
                color: colors.text.primary,
              }}
            >
              <Box component="span" sx={{ fontSize: '24px' }}>{emoji}</Box> {greeting}, {username}
            </Typography>
          )}
        </Box>
        
        {/* Avatar in top right corner */}
        <Avatar
          src={photoUrl || ''}
          alt="User avatar"
          sx={{
            width: isMobile ? '36px' : '40px',
            height: isMobile ? '36px' : '40px',
            backgroundColor: photoUrl ? 'transparent' : '#333333', // Simple gray circle as fallback
          }}
        />
      </Box>
      
      {/* Balance display */}
      {balance !== undefined && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            marginBottom: isMobile ? '8px' : '12px',
            paddingLeft: isMobile ? '4px' : 0,
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: isMobile ? '14px' : '16px',
              color: colors.text.secondary,
            }}
          >
            Бонусный баланс:
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 500,
              color: colors.secondary.main,
            }}
          >
            {balance !== undefined ? Number(balance).toFixed(1) : '0.0'} ★
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
